import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { SESSION_POPUP_FLAG } from "./MpstuntsPopup";

// GLP Weight Management promo — runs from launch through end of August 2026
const GLP_PROMO = {
    id: "glp-199-summer-2026",
    endDate: new Date("2026-09-01T07:00:00Z"), // Sept 1 midnight PT (i.e., end of Aug 31)
    code: "GLP199",
    price: 199,
    ctaHref: "/services/semaglutide",
};

const BENEFITS = [
    "Support healthy weight loss",
    "Help curb appetite & cravings",
    "Improve metabolism & blood sugar",
    "Feel more energized & confident",
];

export default function PromoPopup() {
    // Auto-retire the popup once the promo expires
    const expired = Date.now() >= GLP_PROMO.endDate.getTime();
    const STORAGE_KEY = `zenith_promo_seen_${GLP_PROMO.id}`;

    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (expired) return;
        const seen = localStorage.getItem(STORAGE_KEY);
        const sessionShown = sessionStorage.getItem(SESSION_POPUP_FLAG);
        if (!seen && !sessionShown) {
            const t = setTimeout(() => {
                if (!sessionStorage.getItem(SESSION_POPUP_FLAG)) {
                    sessionStorage.setItem(SESSION_POPUP_FLAG, GLP_PROMO.id);
                    setOpen(true);
                }
            }, 3500);
            return () => clearTimeout(t);
        }
    }, [STORAGE_KEY, expired]);

    if (expired) return null;

    const close = () => {
        localStorage.setItem(STORAGE_KEY, "1");
        setOpen(false);
    };

    const copy = () => {
        const showCopied = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        };
        // Try modern Clipboard API, fall back to legacy execCommand if denied/unsupported.
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(GLP_PROMO.code)
                .then(showCopied)
                .catch(() => legacyCopy(GLP_PROMO.code, showCopied));
        } else {
            legacyCopy(GLP_PROMO.code, showCopied);
        }
    };

    const legacyCopy = (text, onSuccess) => {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            onSuccess?.();
        } catch (_) {
            // Absolute last resort — no clipboard access at all. Just flip state so user sees feedback and can hand-copy.
            onSuccess?.();
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="promo-popup-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
                    onClick={close}
                >
                    <motion.div
                        data-testid="promo-popup-card"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 26 }}
                        className="relative w-full max-w-md bg-[#F2EDE2] rounded-3xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={close}
                            data-testid="promo-popup-close"
                            aria-label="close"
                            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition z-20"
                        >
                            <X size={16} />
                        </button>

                        {/* Header · deep green matches the flyer */}
                        <div className="relative bg-[#0f3a2e] text-[#F2EDE2] px-8 pt-9 pb-7 overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#A89070]/10" />
                            <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#A89070]/8" />
                            <div className="relative">
                                <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9b689] font-medium">
                                    Special Promotion
                                </div>
                                <h3 className="serif text-4xl md:text-[42px] leading-[1.02] mt-3">
                                    Reach your peak<br />
                                    <span className="italic text-[#c9b689] font-light">with </span>
                                    <span className="font-medium">GLP</span>
                                </h3>
                                <p className="mt-3 text-[13px] leading-relaxed text-[#F2EDE2]/85 max-w-[85%]">
                                    Medical weight management to help you thrive.
                                </p>
                            </div>
                        </div>

                        {/* Price highlight */}
                        <div className="relative -mt-4 mx-8 bg-[#F2EDE2] border border-[#A89070]/40 rounded-2xl px-6 py-5 text-center shadow-[0_10px_25px_-8px_rgba(15,58,46,.25)]">
                            <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C7E6A] font-medium">
                                Introductory Price
                            </div>
                            <div className="serif text-6xl leading-none mt-2 text-[#0f3a2e]" data-testid="promo-price">
                                <span className="text-3xl align-top">$</span>{GLP_PROMO.price}
                            </div>
                            <div className="text-xs mt-1 text-[#544638] tracking-wider">for a 1-month supply</div>
                        </div>

                        {/* Benefits */}
                        <div className="px-8 pt-6 pb-2">
                            <ul className="space-y-2.5">
                                {BENEFITS.map((b) => (
                                    <li key={b} className="flex items-start gap-3 text-[13px] text-[#262422]">
                                        <span className="mt-[3px] w-4 h-4 rounded-full bg-[#0f3a2e] flex items-center justify-center shrink-0">
                                            <Check size={10} strokeWidth={3} className="text-[#c9b689]" />
                                        </span>
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Code + CTAs */}
                        <div className="px-8 pt-4 pb-7">
                            <div className="text-center text-[10px] tracking-[0.3em] uppercase text-[#8C7E6A]">
                                Mention at booking or use at checkout
                            </div>
                            <div
                                data-testid="promo-code"
                                className="mt-2 border border-dashed border-[#0f3a2e]/50 rounded-full px-6 py-3.5 font-serif text-2xl tracking-[0.28em] text-[#0f3a2e] bg-white text-center"
                            >
                                {GLP_PROMO.code}
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2.5">
                                <button
                                    onClick={copy}
                                    data-testid="promo-copy-btn"
                                    className="rounded-full py-3 text-[11px] uppercase tracking-[0.22em] bg-[#0f3a2e] text-[#F2EDE2] hover:bg-[#0a2a20] transition"
                                >
                                    {copied ? "Copied" : "Copy Code"}
                                </button>
                                <Link
                                    to={GLP_PROMO.ctaHref}
                                    onClick={close}
                                    data-testid="promo-cta-btn"
                                    className="rounded-full py-3 text-[11px] uppercase tracking-[0.22em] border border-[#0f3a2e]/60 text-[#0f3a2e] hover:bg-[#0f3a2e] hover:text-[#F2EDE2] transition text-center"
                                >
                                    Learn More
                                </Link>
                            </div>

                            <div className="mt-4 text-center text-[10px] leading-relaxed text-[#8C7E6A]">
                                *New patients only. While supplies last. Restrictions apply.<br />
                                Offer valid through August 31, 2026.
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
